// src/ui/components/UnavailabilityModal.js
// Modal to let a team member mark date ranges when they are unavailable.
// Admins/managers can additionally register blocks on behalf of anyone on the team.

import { actions } from "../../app/state.js";
import { listLineupMembers } from "../../services/lineupService.js";
import {
  listUnavailabilityForMember,
  listUnavailabilityForTeam,
  createUnavailability,
  deleteUnavailability,
} from "../../services/memberAvailabilityService.js";
import { onSafeBackdropClick } from "../modalUtils.js";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { Spanish } from "flatpickr/dist/l10n/es.js";

function $(root, selector) {
  return root.querySelector(selector);
}

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function formatDateEs(value) {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatDateTimeEs(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function buildModal({ isManager }) {
  const modal = document.createElement("div");
  modal.className = "modal-backdrop";
  modal.innerHTML = `
    <div class="modal-dialog">
      <div class="modal-header">
        <h3 class="text-lg font-bold text-slate-900">Marcar no disponibilidad</h3>
        <p class="text-sm text-slate-500">Registra fechas en las que no puedes tocar/trabajar.</p>
      </div>
      <div class="modal-body">
        <div class="space-y-3">
          ${isManager ? `
            <div>
              <label class="block text-sm text-slate-600 mb-1">Miembro *</label>
              <select id="unavail-modal-member" class="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"></select>
            </div>
          ` : ""}

          <div>
            <label class="block text-sm text-slate-600 mb-1">Fechas *</label>
            <div class="relative">
              <input id="unavail-modal-range" type="text" readonly placeholder="Selecciona el rango de fechas..."
                class="w-full px-3 py-2 pl-9 rounded-xl border border-slate-200 bg-white text-sm cursor-pointer">
              <i class="fa-solid fa-calendar-days absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
            </div>
            <p class="text-xs text-slate-400 mt-1">Haz clic en el d&iacute;a de inicio y luego en el de fin (o arrastra).</p>
          </div>

          <div>
            <label class="block text-sm text-slate-600 mb-1">Motivo (opcional)</label>
            <input id="unavail-modal-reason" type="text" class="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
              placeholder="Ej: vacaciones, otro bolo, viaje...">
          </div>
        </div>

        <div class="mt-5 pt-4 border-t border-slate-100">
          <div class="flex items-center justify-between gap-2 mb-2">
            <h4 id="unavail-modal-list-title" class="text-sm font-bold text-slate-700">${isManager ? "Próximas fechas del equipo" : "Tus próximas fechas"}</h4>
            <button id="unavail-modal-toggle-history" type="button" class="text-xs font-semibold text-slate-500 hover:text-slate-800 underline underline-offset-2">Ver históricos</button>
          </div>
          ${isManager ? `
            <select id="unavail-modal-filter-member" class="w-full mb-2 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
              title="Ver solo los registros de una persona">
              <option value="">Todas las personas</option>
            </select>
          ` : ""}
          <div id="unavail-modal-list" class="space-y-2 max-h-56 overflow-y-auto">
            <div class="text-sm text-slate-400 text-center py-4">Cargando...</div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button id="unavail-modal-close" class="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 font-medium">Cerrar</button>
        <button id="unavail-modal-save" class="px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 font-medium disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-slate-900" disabled>Guardar</button>
      </div>
    </div>
  `;
  return modal;
}

function renderList(container, rows, { isManager, onDelete, showHistory }) {
  if (!container) return;
  if (!rows.length) {
    container.innerHTML = `<div class="text-sm text-slate-400 text-center py-4">${showHistory ? "Sin fechas pasadas." : "Sin fechas próximas."}</div>`;
    return;
  }

  container.innerHTML = rows.map(row => {
    const createdAt = formatDateTimeEs(row.created_at);
    return `
    <div class="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-slate-50 border border-slate-100${showHistory ? " opacity-75" : ""}">
      <div class="min-w-0">
        ${isManager ? `<div class="text-xs font-bold text-slate-700 truncate">${escapeHtml(row.member_name)}</div>` : ""}
        <div class="text-sm text-slate-800">${formatDateEs(row.date_from)} - ${formatDateEs(row.date_to)}</div>
        ${row.reason ? `<div class="text-xs text-slate-500 truncate">${escapeHtml(row.reason)}</div>` : ""}
        ${createdAt ? `<div class="text-[11px] text-slate-400">Registrado el ${escapeHtml(createdAt)}</div>` : ""}
      </div>
      <button type="button" class="unavail-delete-btn shrink-0 w-8 h-8 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center"
        data-unavail-id="${escapeHtml(row.id)}" title="Eliminar">
        <i class="fa-solid fa-trash-can text-xs"></i>
      </button>
    </div>
  `;
  }).join("");

  container.querySelectorAll(".unavail-delete-btn").forEach(btn => {
    btn.addEventListener("click", () => onDelete(btn.dataset.unavailId));
  });
}

export async function openUnavailabilityModal({ teamId, role, memberId } = {}) {
  const isManager = role === "admin" || role === "manager";
  const modal = buildModal({ isManager });
  document.body.appendChild(modal);

  const rangeInput = $(modal, "#unavail-modal-range");
  const memberSelect = $(modal, "#unavail-modal-member");
  const listContainer = $(modal, "#unavail-modal-list");
  const saveBtn = $(modal, "#unavail-modal-save");
  const closeBtn = $(modal, "#unavail-modal-close");
  const historyBtn = $(modal, "#unavail-modal-toggle-history");
  const listTitle = $(modal, "#unavail-modal-list-title");

  // Por defecto solo las fechas de hoy en adelante; el histórico se abre aparte.
  let showHistory = false;
  let allRows = [];
  // Filtro por persona (solo managers): "" = todas
  const filterSelect = $(modal, "#unavail-modal-filter-member");
  let filterMemberId = "";
  let teamMembers = []; // [{ id, display_name }]

  const fp = rangeInput ? flatpickr(rangeInput, {
    mode: "range",
    dateFormat: "d/m/Y",
    locale: Spanish,
    onChange: (selectedDates) => {
      if (saveBtn) saveBtn.disabled = selectedDates.length === 0;
    },
  }) : null;

  const close = () => {
    fp?.destroy();
    modal.remove();
  };
  closeBtn?.addEventListener("click", close);
  onSafeBackdropClick(modal, close);

  function renderCurrentList() {
    const today = toISODate(new Date());
    const upcoming = allRows.filter(r => (r.date_to || r.date_from) >= today);
    const past = allRows
      .filter(r => (r.date_to || r.date_from) < today)
      .sort((a, b) => String(b.date_from).localeCompare(String(a.date_from))); // más recientes primero

    if (listTitle) {
      listTitle.textContent = showHistory
        ? "Histórico (fechas pasadas)"
        : (isManager ? "Próximas fechas del equipo" : "Tus próximas fechas");
    }
    if (historyBtn) {
      historyBtn.textContent = showHistory ? "Ver próximas" : `Ver históricos (${past.length})`;
    }

    const visibleRows = showHistory ? past : upcoming;
    if (filterSelect) renderMemberFilter(visibleRows);

    renderList(listContainer, filterMemberId ? visibleRows.filter(r => r.member_id === filterMemberId) : visibleRows, {
      isManager,
      showHistory,
      onDelete: async (id) => {
        try {
          await deleteUnavailability(id);
          actions.addToast?.({ type: "success", message: "Fecha eliminada." });
          await refreshList();
        } catch (error) {
          console.error("[UnavailabilityModal] Error deleting", error);
          actions.addToast?.({ type: "error", message: "Error al eliminar la fecha." });
        }
      },
    });
  }

  // Opciones del filtro con el nº de registros (no de días) de cada persona
  // en la vista actual (próximas o histórico).
  function renderMemberFilter(rows) {
    const counts = new Map();
    rows.forEach(r => counts.set(r.member_id, (counts.get(r.member_id) || 0) + 1));

    // Miembros del equipo + cualquiera que tenga registros aunque ya no esté en el lineup
    const people = new Map(teamMembers.map(m => [m.id, m.display_name || "—"]));
    allRows.forEach(r => { if (!people.has(r.member_id)) people.set(r.member_id, r.member_name || "—"); });

    const options = [...people.entries()]
      .sort((a, b) => a[1].localeCompare(b[1], "es"))
      .map(([id, name]) => `<option value="${escapeHtml(id)}">${escapeHtml(name)} (${counts.get(id) || 0})</option>`);

    filterSelect.innerHTML = `<option value="">Todas las personas (${rows.length})</option>` + options.join("");
    filterSelect.value = people.has(filterMemberId) ? filterMemberId : "";
    filterMemberId = filterSelect.value;
  }

  filterSelect?.addEventListener("change", () => {
    filterMemberId = filterSelect.value || "";
    renderCurrentList();
  });

  historyBtn?.addEventListener("click", () => {
    showHistory = !showHistory;
    renderCurrentList();
  });

  async function refreshList() {
    try {
      allRows = isManager
        ? await listUnavailabilityForTeam(teamId)
        : await listUnavailabilityForMember(memberId);
      renderCurrentList();
    } catch (error) {
      console.error("[UnavailabilityModal] Error loading list", error);
      listContainer.innerHTML = `<div class="text-sm text-red-400 text-center py-4">Error al cargar las fechas.</div>`;
    }
  }

  if (isManager && memberSelect) {
    try {
      const members = await listLineupMembers(teamId);
      teamMembers = members;
      memberSelect.innerHTML = members.map(m =>
        `<option value="${escapeHtml(m.id)}">${escapeHtml(m.display_name || "—")}</option>`
      ).join("");
      if (memberId) memberSelect.value = memberId;
    } catch (error) {
      console.error("[UnavailabilityModal] Error loading members", error);
      actions.addToast?.({ type: "error", message: "No se pudo cargar el equipo." });
    }
  }

  await refreshList();

  saveBtn?.addEventListener("click", async () => {
    const targetMemberId = isManager ? (memberSelect?.value || "") : memberId;
    const selectedDates = fp?.selectedDates || [];
    const dateFrom = selectedDates[0] ? toISODate(selectedDates[0]) : "";
    const dateTo = selectedDates[1] ? toISODate(selectedDates[1]) : dateFrom;
    const reason = $(modal, "#unavail-modal-reason")?.value?.trim() || "";

    if (!targetMemberId) {
      actions.addToast?.({ type: "error", message: "Selecciona un miembro." });
      return;
    }
    if (!dateFrom || !dateTo) {
      actions.addToast?.({ type: "error", message: "Indica el rango de fechas." });
      return;
    }

    try {
      saveBtn.disabled = true;
      saveBtn.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin mr-2"></i>Guardando...';
      await createUnavailability({
        team_id: teamId,
        member_id: targetMemberId,
        date_from: dateFrom,
        date_to: dateTo,
        reason,
      });
      actions.addToast?.({ type: "success", message: "No disponibilidad guardada." });
      $(modal, "#unavail-modal-reason").value = "";
      fp?.clear(); // vacía la selección; el botón queda deshabilitado hasta elegir nuevas fechas
      saveBtn.innerHTML = "Guardar";
      await refreshList();
    } catch (error) {
      console.error("[UnavailabilityModal] Error saving", error);
      actions.addToast?.({ type: "error", message: "Error al guardar: " + error.message });
      saveBtn.disabled = false;
      saveBtn.innerHTML = "Guardar";
    }
  });

  return modal;
}

export default { openUnavailabilityModal };
